import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { take, map, catchError } from 'rxjs/operators';

import { LookUpComponent } from '@request/look-up/look-up.component';

import { TAGLookUpEntity } from './tag-lookup-entity';
import { PartsGQL } from '@shared/apollo/queries/parts';
import { ItemsGQL } from '@shared/apollo/queries/item';
import { Item } from '@tag/graphql';

@Injectable({
  providedIn: 'root',
})
export class LookUpService {
  dynamicData: any[] = [];
  dialogRef_: MatDialogRef<any> | undefined;

  private itemCache = new Map<string, { item: Item; timestamp: number }>();
  private readonly CACHE_TTL = 60000;

  constructor(
    public dialog: MatDialog,
    private partsService: PartsGQL,
    private itemsService: ItemsGQL
  ) {}

  lookUpItemByNo(itemNo: string): Observable<Item | undefined> {
    if (!itemNo) {
      return of(undefined);
    }

    const now = Date.now();
    const cached = this.itemCache.get(itemNo);
    if (cached && now - cached.timestamp < this.CACHE_TTL) {
      return of(cached.item);
    }

    return this.itemsService
      .watch({
        filter: `No eq '${itemNo}'`,
        top: 1,
        skip: 0,
      })
      .valueChanges.pipe(
        take(1),
        map((result) => {
          const items = result.data.items.items;
          const item = items.length > 0 ? items[0] : undefined;

          if (item) {
            this.itemCache.set(itemNo, { item, timestamp: now });

            if (this.itemCache.size > 100) {
              this.pruneCache();
            }
          }

          return item;
        }),
        catchError(() => {
          return of(undefined);
        })
      );
  }

  private pruneCache(): void {
    const now = Date.now();
    for (const [key, value] of this.itemCache.entries()) {
      if (now - value.timestamp > this.CACHE_TTL) {
        this.itemCache.delete(key);
      }
    }
  }

  checkPartsAvailabilityQuietly(filter: string | undefined): Observable<any> {
    return this.partsService
      .watch({
        filter: filter ? `Equipment_ID eq '${filter}'` : '',
        top: 1,
        skip: 0,
      })
      .valueChanges.pipe(
        take(1),
        map((result) => result.data.parts)
      );
  }

  lookUp<T>(
    entity: TAGLookUpEntity,
    filteredValue?: string | null,
    eventHandlers?: {
      barcodeScanned?: (code: string) => void;
      skipDialog?: boolean;
    }
  ): Observable<Record<any, any> | undefined> {
    if (eventHandlers?.skipDialog) {
      return this.checkPartsAvailabilityQuietly(filteredValue || undefined);
    }

    return new Observable<Record<any, any> | undefined>((observer) => {
      const dialogRef = this.dialog.open(LookUpComponent, {
        panelClass: ['Zoom-out', 'animated', 'dynamic-look-up'],
        data: { entity, filteredValue },
        position: { top: '100px' },
      });

      if (eventHandlers) {
        const instance = dialogRef.componentInstance;
        if (eventHandlers.barcodeScanned) {
          instance.barcodeScanned.subscribe(eventHandlers.barcodeScanned);
        }
      }

      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((result) => {
          observer.next(result);
          observer.complete();
        });
    });
  }
}
